import { chaletHistory } from "chalet-shared";
import * as I from "immutable";
import { createSelector } from "reselect";

import { Action, actionCreators } from "../actions";
import {
    Data,
    PageTextExtendedRecord,
    PageTextExtendedRecordFactory,
    PageTextRecord,
    PageTextRecordFactory,
    SearchTextRecord,
    SearchTextRecordFactory,
} from "../service/Data";
import { notUndefined } from "../utils/any";
import {
    listFilter,
    listFirst,
    listReduce,
    orderedMapUpdate,
    orderedMapUpdateExisting,
    sameSet,
    shallowSameList,
    shallowSameMap,
} from "../utils/immutable";

import { RootState } from ".";

export const LOAD_ROOM_HISTORY_PAGE_SIZE = 30;
export const LOAD_ROOM_HISTORY_MAX_PAGE_SIZE = 100;

export const MAX_COLS = 3;
export const MAX_ROWS = 3;
export const MAX_OPEN_ROOMS_NUM = MAX_COLS * MAX_ROWS; // 12 * 6 seems like biggest potentially usable value

export enum RoomType {
    REAL_TIME = "realTime",
    ROOM_SEARCH = "search",
    BACK = "back",
    HISTORY = "history",
    GLOBAL_SEARCH = "globalSearch",
    ACTIVITY_LOG = "activityLog",
}

type roomId = string;
type viewId = string;
export interface UploadInProgress {
    tag: string;
    state: "uploading" | "abort";
    progress: number; // 0..1
    file: Blob;
}

export interface Uploaded {
    tag: string;
    state: "uploaded";
    file: Blob;
    data: Data.UploadData;
}

export type Uploading = Uploaded | UploadInProgress;

export type ScrollPosition = "initial" | "unread" | "bottom" | "history-message" | "reset";

export type ViewContinuity = "initial" | "first" | "middle" | "last";

export interface EditModeInfo {
    message: PageTextExtendedRecord;
    sending?: boolean;
    error?: boolean;
    expired?: boolean;
}

export type RoomViewProps = {
    roomId: roomId;
    viewId: viewId;
    type: RoomType;
    inputIsActive: boolean;
    needsFocus: boolean;
    canJumpToBottom: boolean;
    canJumpToUnread: boolean;
    scrollPosition: ScrollPosition;
    scrollMessageId?: string;
    searchQuery?: string;
    historyAroundTs?: number;
    historyHighlighedMessage?: string; // parsed text of message for historyAroundTs
    editMode: EditModeInfo | undefined;
};
export const RoomView = I.Record<RoomViewProps>({
    roomId: "",
    viewId: "",
    type: RoomType.REAL_TIME,
    inputIsActive: false,
    needsFocus: false,
    canJumpToBottom: false,
    canJumpToUnread: false,
    scrollPosition: "initial",
    scrollMessageId: undefined,
    searchQuery: undefined,
    historyAroundTs: undefined,
    historyHighlighedMessage: undefined,
    editMode: undefined,
});
export type RoomView = I.Record<RoomViewProps> & RoomViewProps;

export type RoomViews = I.OrderedMap<roomId, I.OrderedMap<viewId, RoomView>>;

export type MentionsOrder = I.OrderedSet<{ id: string; silentMention: boolean | undefined }>;

const byOpenRoomDefault = I.Record({
    roomSearchSpinner: "initial" as "initial" | "spinner" | "loading" | "loaded",
    roomSearchResults: [] as SearchTextRecord[],
    roomSearchCursor: undefined as number | undefined,

    loadRoomHistoryPossibleTop: true,
    loadRoomHistoryPossibleBottom: true,
    loadRoomHistorySpinner: undefined as "spinner" | "loading" | undefined,
    loadRoomHistoryResults: [] as PageTextRecord[],

    joinedRoomHistoryPossibleTop: true,
    joinedRoomHistoryPossibleBottom: true,
    joinedRoomHistorySpinner: undefined as "spinner" | "loading" | undefined,
    joinedRoomHistory: I.List<PageTextExtendedRecord>(),
    joinedRoomHistoryFirstUnreadTs: 0,

    roomMentionsOrder: I.OrderedSet() as MentionsOrder,
    roomMentionsLoading: 0,
    roomMentionsSelected: undefined as undefined | string,
    roomMentionsIsOpen: false,
    roomMentionsInputPrefix: undefined as undefined | string,

    addMemberInput: "",
})();

const initialStore = I.Record({
    sleepingViewId: undefined as string | undefined,
    activeViewIds: I.List<viewId>(),
    roomToOpen: undefined as RoomView | undefined,
    roomViews: I.OrderedMap() as RoomViews,
    byOpenRoom: I.Map<string, typeof byOpenRoomDefault>(),
    fitCols: MAX_COLS,
    fitRows: MAX_ROWS,
    rosterHoveredRoomId: undefined as string | undefined,
})();
type Store = typeof initialStore;

export function update(store = initialStore, action: Action): Store {
    switch (action.type) {
        case actionCreators.onLogout.type:
        case actionCreators.onWrongToken.type: {
            return initialStore;
        }
        case actionCreators.setRoomViewAsSleeping.type: {
            return store.merge({
                sleepingViewId: action.roomView ? action.roomView.viewId : undefined,
            });
        }
        case actionCreators.onRoomViewFocus.type: {
            const room = action.payload;
            return store.merge({
                activeViewIds: updateActiveViews(store.activeViewIds, room.viewId),
                roomViews: updateActive(store.roomViews, room, true),
                sleepingViewId: undefined,
            });
        }
        case actionCreators.onRoomViewBlur.type: {
            const room = action.payload;
            return store.merge({
                roomViews: updateView(store.roomViews, room, view =>
                    view.merge({ inputIsActive: false })
                ),
            });
        }
        case actionCreators.restoreRooms.type: {
            // let's focus last focused room
            const { activeRooms, roomViews } = action.payload;
            const roomToFocus = activeRooms.first<undefined>();
            let { byOpenRoom } = store;
            byOpenRoom = flattenRoomViews(roomViews).reduce(
                (acc, { viewId, searchQuery, historyAroundTs }) => {
                    if (searchQuery || historyAroundTs) {
                        return acc.update(viewId, byOpenRoomDefault, x =>
                            x.merge({
                                roomSearchSpinner: searchQuery ? "spinner" : undefined,
                                loadRoomHistorySpinner: historyAroundTs ? "spinner" : undefined,
                            })
                        );
                    } else {
                        return acc;
                    }
                },
                byOpenRoom
            );
            return store.merge({
                activeViewIds: activeRooms.map(x => x.viewId),
                roomViews: updateFocus(roomViews, roomToFocus, true),
                byOpenRoom,
            });
        }
        case actionCreators.setRoomViewAsActive.type: {
            const { roomView, focus } = action.payload;
            return store.merge({
                activeViewIds: updateActiveViews(store.activeViewIds, roomView.viewId),
                roomViews: updateFocus(store.roomViews, roomView, focus),
                sleepingViewId: undefined,
            });
        }
        case actionCreators.resetRoomViewToFocus.type: {
            return store.merge({
                roomViews: mapAllViews(store.roomViews, x => x.merge({ needsFocus: false })),
            });
        }

        case actionCreators.setRoomToOpen.type: {
            return store.merge({
                roomToOpen: action.payload,
            });
        }
        case actionCreators.openRoomSucceeded.type: {
            let { activeViewIds, roomViews } = store;
            const { roomView } = action.payload;
            const view = RoomView(
                roomView.merge({
                    needsFocus: true,
                })
            );
            roomViews = addNewView(roomViews, view);
            activeViewIds = updateActiveViews(activeViewIds, view.viewId);
            return store.merge({
                roomViews,
                activeViewIds,
                roomToOpen: undefined,
                sleepingViewId: undefined,
            });
        }
        case actionCreators.closeRoomSucceeded.type: {
            // delete this._roomsScrollPositions[roomId];
            // delete this._roomsCanJumpToBottom[roomId];
            let { activeViewIds, roomViews, byOpenRoom, sleepingViewId } = store;
            const roomsToClose = action.payload;
            const notInRoomsToClose = (x: string) => !roomsToClose.some(v => v.viewId === x);
            roomsToClose.forEach(view => {
                roomViews = removeView(roomViews, view);
                byOpenRoom = byOpenRoom.remove(view.viewId);
            });
            activeViewIds = listFilter(activeViewIds, notInRoomsToClose);
            const topActiveView = listFirst(activeViewIds);
            const hasBeenSleeping = !!sleepingViewId;
            sleepingViewId = undefined;
            if (topActiveView) {
                const activeView = flattenRoomViews(roomViews).find(
                    x => x.viewId === topActiveView
                );
                roomViews = updateFocus(roomViews, activeView, !hasBeenSleeping);
                if (hasBeenSleeping) {
                    // preserve sleeping state for new view
                    sleepingViewId = activeView && activeView.viewId;
                }
            }
            return store.merge({
                roomViews,
                byOpenRoom,
                activeViewIds,
                sleepingViewId,
            });
        }
        case actionCreators.setRoomViewEditMode.type: {
            const { roomView, message } = action.payload;
            return store.merge({
                roomViews: updateView(
                    updateFocus(store.roomViews, roomView, true),
                    roomView,
                    view => view.merge({ editMode: { message } })
                ),
            });
        }
        case actionCreators.updateRoomViewEditMode.type: {
            const { roomView, editMode } = action.payload;
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, view => view.merge({ editMode })),
            });
        }
        case actionCreators.unsetRoomViewEditMode.type: {
            const { roomView } = action.payload;
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, view =>
                    view.merge({ editMode: undefined })
                ),
            });
        }
        case actionCreators.roomSearchQueryUpdated.type: {
            const { roomView, searchQuery } = action.payload;
            const { roomViews } = store;
            return store.merge({
                roomViews: updateView(roomViews, roomView, x => x.merge({ searchQuery })),
            });
        }

        case actionCreators.roomSearchRequested.type: {
            const { roomView, cursor } = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge(
                        cursor
                            ? {
                                  roomSearchSpinner: "loading",
                              }
                            : {
                                  roomSearchSpinner: "spinner",
                                  roomSearchResults: [],
                                  roomSearchCursor: undefined,
                              }
                    )
                ),
            });
        }
        case actionCreators.roomSearchSucceeded.type: {
            const { roomView, data } = action.payload;
            const results = data.page.map(x => SearchTextRecordFactory(x));
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        roomSearchSpinner: "loaded",
                        roomSearchResults: old.roomSearchResults.concat(results),
                        roomSearchCursor: data.cursor,
                    })
                ),
            });
        }

        case actionCreators.loadRoomHistoryRequested.type: {
            const { roomView, how } = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({ loadRoomHistorySpinner: how === "around" ? "spinner" : "loading" })
                ),
            });
        }

        case actionCreators.loadRoomHistory.type: {
            const { roomView, how, data } = action.payload;
            const results = data.page.map(x => PageTextRecordFactory(x));
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, x =>
                    x.merge({
                        scrollPosition: "history-message",
                    })
                ),
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old => {
                    let out = old.merge({
                        loadRoomHistorySpinner: undefined,
                    });
                    if (how === "lt") {
                        out = out.merge({
                            loadRoomHistoryPossibleTop:
                                results.length >= LOAD_ROOM_HISTORY_PAGE_SIZE,
                            loadRoomHistoryResults: results
                                .reverse()
                                .concat(out.loadRoomHistoryResults),
                        });
                    } else if (how === "gt") {
                        out = out.merge({
                            loadRoomHistoryPossibleBottom:
                                results.length >= LOAD_ROOM_HISTORY_PAGE_SIZE,
                            loadRoomHistoryResults: out.loadRoomHistoryResults.concat(
                                results.reverse()
                            ),
                        });
                    } else if (how === "around") {
                        out = out.merge({
                            loadRoomHistoryResults: addHighlightToHistoryResults(
                                roomView,
                                results.reverse()
                            ),
                        });
                    }
                    return out;
                }),
            });
        }

        case actionCreators.loadJoinedRoomHistoryRequested.type: {
            const { roomView, how } = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        joinedRoomHistorySpinner: how === "around" ? "spinner" : "loading",
                    })
                ),
            });
        }

        case actionCreators.loadJoinedRoomHistory.type: {
            const { roomView, how, data } = action.payload;
            const results = I.List(data.page.map(x => PageTextExtendedRecordFactory(x)));
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old => {
                    const incomingHistoryIds = results.map(x => x.id).toSet();
                    const newHistory = old.joinedRoomHistory
                        .filter(existing => {
                            return !incomingHistoryIds.includes(existing.id);
                        })
                        .concat(results)
                        .sort((a, b) => a.ts - b.ts);

                    let out = old.merge({
                        joinedRoomHistorySpinner: undefined,
                    });
                    if (how === "lt") {
                        out = out.merge({
                            joinedRoomHistoryPossibleTop:
                                results.size >= LOAD_ROOM_HISTORY_PAGE_SIZE,
                            joinedRoomHistory: newHistory,
                        });
                    } else if (how === "gt") {
                        out = out.merge({
                            joinedRoomHistoryPossibleBottom:
                                results.size >= LOAD_ROOM_HISTORY_PAGE_SIZE,
                            joinedRoomHistory: newHistory,
                        });
                    } else if (how === "around") {
                        out = out.merge({
                            joinedRoomHistory: newHistory,
                        });
                    }
                    if (results.some(x => !!x.unseen)) {
                        out = out.merge({
                            joinedRoomHistoryFirstUnreadTs: findFirstUnread(out.joinedRoomHistory),
                        });
                    }
                    return out;
                }),
            });
        }

        case actionCreators.onJoinedRoom.type: {
            const { roomId, data, failedMessages } = action.payload;
            let joinedRoomHistory = I.List(
                data.page.map(x => PageTextExtendedRecordFactory(x))
            ).reverse();
            failedMessages.forEach(x => {
                if (x.roomId === roomId) {
                    joinedRoomHistory = joinedRoomHistory.push(
                        PageTextExtendedRecordFactory({
                            id: x.id,
                            author_user: x.author_user,
                            ts: +new Date() * 1000,
                            __meta__: { hasFailed: true, pendingRaw: x.text },
                        })
                    );
                }
            });
            const joinedRoomHistoryFirstUnreadTs = findFirstUnread(joinedRoomHistory);
            const hasUnread = !!joinedRoomHistoryFirstUnreadTs;
            return updateByRealTimeRoomId(
                store,
                roomId,
                old => {
                    return old.merge({
                        joinedRoomHistory,
                        joinedRoomHistoryFirstUnreadTs,
                    });
                },
                hasUnread
                    ? old => {
                          return old.merge({
                              scrollPosition: joinedRoomHistoryFirstUnreadTs
                                  ? "unread"
                                  : old.scrollPosition,
                          });
                      }
                    : undefined
            );
        }

        case actionCreators.updateRoomFirstUnread.type: {
            const { roomId } = action.payload;
            return updateByRealTimeRoomId(store, roomId, old =>
                old.merge({
                    joinedRoomHistoryFirstUnreadTs: findFirstUnread(old.joinedRoomHistory),
                })
            );
        }

        case actionCreators.sendRoomMessage.type: {
            const { roomView, pendingId, monolog, raw, ts } = action;
            const sendingMessage: Data.PageText = {
                id: pendingId,
                ts,
                parsed: raw,
                author_user: {
                    id: (monolog && monolog.userId) || `sending-author-${ts}`,
                    name: (monolog && monolog.name) || "",
                    email: (monolog && monolog.userEmail) || "",
                    avatar_url: (monolog && monolog.avatarUrl) || null,
                },
            };
            if (roomView) {
                const message = PageTextExtendedRecordFactory({
                    ...sendingMessage,
                    __meta__: { hasFailed: false, pendingRaw: raw },
                });
                return store.merge({
                    byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old => {
                        return old.merge({
                            joinedRoomHistory: old.joinedRoomHistory
                                .filter(({ id }) => id !== message.id)
                                .concat([message]),
                        });
                    }),
                });
            }
            return store;
        }

        case actionCreators.sendRoomMessageFailed.type: {
            const { roomView, pendingId } = action;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old => {
                    return old.merge({
                        joinedRoomHistory: old.joinedRoomHistory.map(x => {
                            if (x.id === pendingId) {
                                return x.merge({ __meta__: { ...x.__meta__, hasFailed: true } });
                            }
                            return x;
                        }),
                    });
                }),
            });
        }

        case actionCreators.discardFailedRoomMessage.type: {
            const { roomView, pendingId } = action;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old => {
                    return old.merge({
                        joinedRoomHistory: old.joinedRoomHistory.filter(x => {
                            return x.id !== pendingId;
                        }),
                    });
                }),
            });
        }

        case actionCreators.updateRoomMessage.type: {
            const { roomView } = action;
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, view =>
                    view.merge({
                        editMode: view.editMode
                            ? { ...view.editMode, sending: true, error: undefined }
                            : undefined,
                    })
                ),
            });
        }

        case actionCreators.deleteRoomMessage.type: {
            const { roomView, messageId } = action;
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, view =>
                    view.merge({
                        editMode:
                            view.editMode && view.editMode.message.id === messageId
                                ? undefined
                                : view.editMode,
                    })
                ),
            });
        }

        case actionCreators.onRoomText.type: {
            const { data, pendingId } = action.payload;
            const { room_id: roomId } = data;

            const message = PageTextExtendedRecordFactory(data);
            let resetsFirstUnread = false;
            const updater2 = (view: RoomView) => {
                resetsFirstUnread = !view.inputIsActive;
                return view;
            };
            return updateByRealTimeRoomId(
                store,
                roomId,
                old => {
                    let { joinedRoomHistory } = old;
                    if (pendingId) {
                        joinedRoomHistory = joinedRoomHistory.map(x => {
                            if (x.id === pendingId) {
                                return message;
                            } else {
                                return x;
                            }
                        });
                    } else {
                        joinedRoomHistory = joinedRoomHistory.concat([message]);
                    }
                    return old.merge({
                        joinedRoomHistory,
                        joinedRoomHistoryFirstUnreadTs: resetsFirstUnread
                            ? findFirstUnread(joinedRoomHistory)
                            : old.joinedRoomHistoryFirstUnreadTs,
                    });
                },
                updater2
            );
        }

        case actionCreators.onUpdateRoomText.type: {
            const { data } = action.payload;
            const {
                id: messageId,
                room_id: roomId,
                version,
                last_update_ts,
                parsed,
                raw,
                // @ts-ignore
                quoted,
            } = data;

            return updateByRealTimeRoomId(store, roomId, old => {
                let { joinedRoomHistory } = old;
                const oldMessageIndex = joinedRoomHistory.findIndex(x => x.id === messageId);
                const oldMessage = joinedRoomHistory.get(oldMessageIndex);
                if (oldMessage) {
                    const newMessage = oldMessage.merge({
                        parsed,
                        quoted,
                        raw,
                        version,
                        last_update_ts,
                    });
                    joinedRoomHistory = joinedRoomHistory.set(oldMessageIndex, newMessage);
                }
                return old.merge({
                    joinedRoomHistory,
                });
            });
        }

        case actionCreators.onDeleteRoomText.type: {
            const { data } = action.payload;
            const { id: messageId, room_id: roomId, last_update_ts: lastUpdateTs } = data;

            return updateByRealTimeRoomId(store, roomId, old => {
                let { joinedRoomHistory } = old;
                const oldMessageIndex = joinedRoomHistory.findIndex(x => x.id === messageId);
                const oldMessage = joinedRoomHistory.get(oldMessageIndex);
                if (oldMessage) {
                    const newMessage = oldMessage.merge({
                        parsed: "",
                        shared_files: [],
                        last_update_ts: lastUpdateTs,
                        deleted: true,
                    });
                    joinedRoomHistory = joinedRoomHistory.set(oldMessageIndex, newMessage);
                }
                return old.merge({
                    joinedRoomHistory,
                });
            });
        }

        case actionCreators.onReactionUpdate.type: {
            const { data } = action.payload;
            const { text_id: messageId, room_id: roomId, reactions } = data;

            return updateByRealTimeRoomId(store, roomId, old => {
                let { joinedRoomHistory } = old;
                const oldMessageIndex = joinedRoomHistory.findIndex(x => x.id === messageId);
                const oldMessage = joinedRoomHistory.get(oldMessageIndex);
                if (oldMessage) {
                    const newMessage = oldMessage.merge({
                        reactions,
                    });
                    joinedRoomHistory = joinedRoomHistory.set(oldMessageIndex, newMessage);
                }
                return old.merge({
                    joinedRoomHistory,
                });
            });
        }

        case actionCreators.onSeenText.type: {
            const data = action.payload;
            if (Data.isSeenTextAll(data)) {
                const cleanByRoomId = (roomId: string, acc: Store) => {
                    return updateByRealTimeRoomId(acc, roomId, old =>
                        old.merge({
                            joinedRoomHistory: old.joinedRoomHistory.map(chat => {
                                if (chat.unseen) {
                                    return chat.merge({ unseen: false });
                                } else {
                                    return chat;
                                }
                            }),
                            joinedRoomHistoryFirstUnreadTs: 0,
                        })
                    );
                };
                if (data.room_id) {
                    return cleanByRoomId(data.room_id, store);
                } else {
                    // clean each room
                    return store.roomViews
                        .keySeq()
                        .toArray()
                        .reduce((storeAcc, roomId) => cleanByRoomId(roomId, storeAcc), store);
                }
            } else {
                const { tss, room_id: roomId } = data;
                return updateByRealTimeRoomId(store, roomId, old =>
                    old.merge({
                        joinedRoomHistory: old.joinedRoomHistory.map(chat => {
                            if (tss.indexOf(chat.ts) !== -1) {
                                return chat.merge({ unseen: false });
                            } else {
                                return chat;
                            }
                        }),
                    })
                );
            }
        }

        case actionCreators.onSeenAllText.type: {
            const update = (old: typeof byOpenRoomDefault) =>
                old.merge({
                    joinedRoomHistory: old.joinedRoomHistory.map(chat => {
                        if (chat.unseen) {
                            return chat.merge({ unseen: false });
                        } else {
                            return chat;
                        }
                    }),
                    joinedRoomHistoryFirstUnreadTs: 0,
                });

            if (action.payload.room_ids === undefined) {
                return store.merge({
                    byOpenRoom: store.byOpenRoom.map(update),
                });
            }
            const { room_ids: roomIds } = action.payload;
            const cleanByRoomId = (roomId: string, acc: Store) => {
                return updateByRealTimeRoomId(acc, roomId, update);
            };

            return roomIds.reduce((storeAcc, roomId) => cleanByRoomId(roomId, storeAcc), store);
        }

        case actionCreators.onUnseenText.type: {
            const { room_id: roomId, tss } = action.payload;
            return updateByRealTimeRoomId(store, roomId, old =>
                old.merge({
                    joinedRoomHistory: old.joinedRoomHistory.map(chat => {
                        if (tss.indexOf(chat.ts) !== -1) {
                            return chat.merge({ unseen: true });
                        } else {
                            return chat;
                        }
                    }),
                })
            );
        }

        case actionCreators.canJumpToBottomUpdated.type: {
            const { roomView, value: canJumpToBottom } = action.payload;
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, x => x.merge({ canJumpToBottom })),
            });
        }
        case actionCreators.canJumpToUnreadUpdated.type: {
            const { roomView, value: canJumpToUnread } = action.payload;
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, x => x.merge({ canJumpToUnread })),
            });
        }
        case actionCreators.roomScrollPositionUpdated.type: {
            const { roomView, value: scrollPosition, messageId: scrollMessageId } = action.payload;
            return store.merge({
                roomViews: updateView(store.roomViews, roomView, x =>
                    x.merge({ scrollPosition, scrollMessageId })
                ),
            });
        }

        case actionCreators.setFitRooms.type: {
            return store.merge({
                fitCols: action.cols,
                fitRows: action.rows,
            });
        }

        case actionCreators.setRosterHoveredRoomId.type: {
            return store.merge({
                rosterHoveredRoomId: action.payload,
            });
        }

        case actionCreators.onRosterSearchLoading.type: {
            const roomView = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        roomMentionsLoading: old.roomMentionsLoading + 1,
                    })
                ),
            });
        }
        case actionCreators.onRosterSearchLoaded.type: {
            const roomView = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        roomMentionsLoading: Math.max(old.roomMentionsLoading - 1, 0),
                    })
                ),
            });
        }

        case actionCreators.onMentionsOrderUpdate.type: {
            const { roomView, data } = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        roomMentionsOrder: I.OrderedSet(
                            (data.rooms || []).map(({ id, silent_mention: silentMention }) => ({
                                id,
                                silentMention,
                            }))
                        ),
                    })
                ),
            });
        }

        case actionCreators.roomMentionSelected.type: {
            const { roomView, roomId } = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        roomMentionsSelected: roomId,
                    })
                ),
            });
        }

        case actionCreators.roomMentionsSet.type: {
            const { roomView, isOpen, prefix } = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        roomMentionsSelected: undefined,
                        roomMentionsIsOpen: isOpen,
                        roomMentionsInputPrefix: prefix,
                    })
                ),
            });
        }

        case actionCreators.privateRoomAddMembersInputSet.type: {
            const { roomView, prefix } = action.payload;
            return store.merge({
                byOpenRoom: store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, old =>
                    old.merge({
                        roomMentionsSelected: undefined,
                        addMemberInput: prefix,
                    })
                ),
            });
        }

        default: {
            return store;
        }
    }
}

function updateView(
    roomViews: RoomViews,
    view: { roomId: string; viewId: string } | undefined,
    updater: (value: RoomView) => RoomView
) {
    if (view) {
        const { roomId, viewId } = view;
        return orderedMapUpdateExisting(roomViews, roomId, x =>
            orderedMapUpdateExisting(x, viewId, updater)
        );
    }
    return roomViews;
}

function updateFocus(
    roomViews: RoomViews,
    view: { roomId: string; viewId: string } | undefined,
    focus: boolean
) {
    const out = mapAllViews(roomViews, x => x.merge({ needsFocus: false }));
    return updateView(out, view, x => x.merge({ needsFocus: focus }));
}

function updateActive(
    roomViews: RoomViews,
    view: { roomId: string; viewId: string } | undefined,
    active: boolean
) {
    const out = mapAllViews(roomViews, x => x.merge({ inputIsActive: false }));
    return updateView(out, view, x => x.merge({ inputIsActive: active }));
}

function mapAllViews(roomViews: RoomViews, updater: (value: RoomView) => RoomView) {
    return roomViews.map(x => x.map(updater));
}

function updateActiveViews(activeViews: I.List<viewId>, viewId: viewId) {
    if (listFirst(activeViews) === viewId) {
        return activeViews; // already there
    } else {
        const newValue = listFilter(activeViews, x => x !== viewId);
        return newValue.unshift(viewId);
    }
}

export function removeView(roomViews: RoomViews, view: RoomView) {
    return orderedMapUpdate(roomViews, view.roomId, I.OrderedMap(), views =>
        views.remove(view.viewId)
    ).filter(x => x.size > 0);
}

export function addNewView(roomViews: RoomViews, view: RoomView) {
    return orderedMapUpdate(roomViews, view.roomId, I.OrderedMap(), views => {
        if (view.type === RoomType.REAL_TIME && views.size > 0) {
            // real time view has to be inserted into begining
            return I.OrderedMap({ [view.viewId]: view }).merge(views);
        }
        return views.set(view.viewId, view);
    });
}

export function flattenRoomViews(roomViews: RoomViews) {
    return roomViews.toList().flatMap(x => x.toList());
}

export function unflattenRoomViews(openRooms: I.List<RoomView>) {
    return listReduce(openRooms, addNewView, I.OrderedMap());
}

export function addHighlightToHistoryResults(
    { viewId, historyAroundTs, historyHighlighedMessage }: RoomView,
    results: PageTextRecord[]
) {
    if (historyAroundTs && historyHighlighedMessage) {
        return results.map(message => {
            if (message.ts === historyAroundTs) {
                return message.merge({ parsed: historyHighlighedMessage });
            }
            return message;
        });
    }
    return results;
}

function updateByRealTimeRoomId(
    store: Store,
    roomId: roomId,
    updater?: (value: typeof byOpenRoomDefault) => typeof byOpenRoomDefault,
    updater2?: (value: RoomView) => RoomView
) {
    // we are using the fact that updater2 is runing before updater
    const roomView = flattenRoomViews(store.roomViews).find(
        x => x.roomId === roomId && x.type === RoomType.REAL_TIME
    );
    if (roomView) {
        return store.merge({
            roomViews: updater2 ? updateView(store.roomViews, roomView, updater2) : store.roomViews,
            byOpenRoom: updater
                ? store.byOpenRoom.update(roomView.viewId, byOpenRoomDefault, updater)
                : store.byOpenRoom,
        });
    }
    return store;
}

function findFirstUnread(joinedRoomHistory: typeof byOpenRoomDefault["joinedRoomHistory"]) {
    const firstUnread = joinedRoomHistory.find(chat => !!chat.unseen);
    return firstUnread ? firstUnread.ts : 0;
}

export type OuterStore = { rooms: Store };

export function getRoomViews(store: OuterStore) {
    return store.rooms.roomViews;
}

export function getRoomViewIsOpen(store: OuterStore, { roomId, viewId }: RoomView) {
    const x = getRoomViews(store).get(roomId);
    return (x && x.has(viewId)) || false;
}

export const getFlattenRoomViews = createSelector(
    getRoomViews,
    views => {
        return flattenRoomViews(views);
    }
);

const getRoomViewsWithContinuityCache = shallowSameList<{
    viewContinuity: ViewContinuity;
    viewId: string;
    roomId: string;
    type: RoomType;
}>();

export const getRoomViewsWithContinuity = createSelector(
    getRoomViews,
    roomViews =>
        getRoomViewsWithContinuityCache(
            roomViews.toList().flatMap(roomViewsForRoom => {
                const size = roomViewsForRoom.size;
                return roomViewsForRoom.toList().map((roomView, i) => {
                    let viewContinuity: ViewContinuity = "initial";
                    if (size > 1) {
                        if (i === 0) {
                            viewContinuity = "first";
                        } else if (i + 1 === size) {
                            viewContinuity = "last";
                        } else {
                            viewContinuity = "middle";
                        }
                    }
                    return {
                        viewContinuity,
                        viewId: roomView.viewId,
                        roomId: roomView.roomId,
                        type: roomView.type,
                    };
                });
            })
        )
);

export const selectRogueViewIds = createSelector(
    getRoomViewsWithContinuity,
    views =>
        views
            .map(({ viewContinuity, ...roomView }) =>
                roomView.type === RoomType.ROOM_SEARCH &&
                (viewContinuity === "first" || viewContinuity === "initial")
                    ? roomView.viewId
                    : undefined
            )
            .filter(notUndefined)
            .toSet()
);

export function getIsRogue(store: OuterStore, { viewId, type }: RoomView) {
    if (type === RoomType.ROOM_SEARCH) {
        return selectRogueViewIds(store).has(viewId);
    } else {
        return false;
    }
}

export const getActiveViewId = (store: OuterStore) => {
    return store.rooms.activeViewIds.first<undefined>();
};

export const getNotSleepingActiveViewId = (store: OuterStore) => {
    const viewId = getActiveViewId(store);
    const { sleepingViewId } = store.rooms;
    if (sleepingViewId && sleepingViewId === viewId) {
        return undefined;
    }
    return viewId;
};

export const getActiveRoomView = createSelector(
    getFlattenRoomViews,
    getActiveViewId,
    (openRooms, activeViewId) =>
        activeViewId ? openRooms.find(x => x.viewId === activeViewId) : undefined
);

const getOpenRoomIdsCache = sameSet<string>();

export const getOpenRoomIds = createSelector(
    getFlattenRoomViews,
    rooms => {
        return getOpenRoomIdsCache(
            I.Set(rooms.filter(x => x.type === RoomType.REAL_TIME).map(x => x.roomId))
        );
    }
);

export function getRealTimeViewByRoomId(state: RootState, roomId: string) {
    const views = getFlattenRoomViews(state);
    return views.find(x => x.type === RoomType.REAL_TIME && x.roomId === roomId);
}

const openRoomCheckersCache = shallowSameMap<string, RoomCheckers>();

export type RoomCheckers = { h: number; w: number; mh: number; mw: number; sh: number };
export const openRoomCheckers = createSelector(
    getFlattenRoomViews,
    getFitCols,
    (rooms, cols) => {
        const mh = Math.min(rooms.size, cols);
        const mw = Math.ceil(rooms.size / (mh || 1));
        let roomPositions = I.Map<string, RoomCheckers>();
        rooms.forEach((x, i) => {
            if (x.type === RoomType.REAL_TIME) {
                const w = 1 + Math.floor(i / mh);
                const h = 1 + (i % mh);
                const sh = i + 1 === rooms.size ? 1 + mh * mw - rooms.size : 1;
                roomPositions = roomPositions.set(x.roomId, { h, w, mh, mw, sh });
            }
        });
        return openRoomCheckersCache(roomPositions);
    }
);

export function getSetRoomToOpen(store: OuterStore) {
    return store.rooms.roomToOpen;
}

export function getRoomSearchIsLoading(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).roomSearchSpinner;
}

export const selectByOpenRoom = (viewId: string) =>
    createSelector(
        (store: OuterStore) => {
            return store.rooms.byOpenRoom;
        },
        byOpenRoom => byOpenRoom.get(viewId) || byOpenRoomDefault
    );

export const selectRoomSearchIsLoading = ({ viewId }: RoomView) => {
    const selectorByView = selectByOpenRoom(viewId);
    return createSelector(
        selectorByView,
        x => x.roomSearchSpinner
    );
};

export const selectRoomSearchResults = ({ viewId }: RoomView) => {
    const selectorByView = selectByOpenRoom(viewId);
    return createSelector(
        selectorByView,
        x => x.roomSearchResults
    );
};

export const selectRoomSearchCursor = ({ viewId }: RoomView) => {
    const selectorByView = selectByOpenRoom(viewId);
    return createSelector(
        selectorByView,
        x => x.roomSearchCursor
    );
};

export function getRoomHistoryPossibleTop(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).loadRoomHistoryPossibleTop;
}

export function getRoomHistoryPossibleBottom(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).loadRoomHistoryPossibleBottom;
}

export function getRoomHistoryIsLoading(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).loadRoomHistorySpinner;
}

export function getRoomHistoryResults(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).loadRoomHistoryResults;
}

export function getJoinedRoomHistoryPossibleTop(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).joinedRoomHistoryPossibleTop;
}

export function getJoinedRoomHistoryPossibleBottom(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).joinedRoomHistoryPossibleBottom;
}

export function getJoinedRoomHistoryIsLoading(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).joinedRoomHistorySpinner;
}

export function getJoinedRoomHistory(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).joinedRoomHistory;
}

// Returns ts of first unread message, that is fixed on joining room. Used for "New messages" marker
export function getJoinedRoomHistoryFirstUnreadTs(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).joinedRoomHistoryFirstUnreadTs;
}

// Return first unread message. Used for "Jump to unread". This value changes on scrolling
export function getActualFirstUnread(store: OuterStore, { viewId }: RoomView) {
    const history = store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).joinedRoomHistory;
    return history.find(x => x.unseen === true);
}

export const makeSelectJoinedRoomMessages = () =>
    createSelector(
        getJoinedRoomHistory,
        getJoinedRoomHistoryFirstUnreadTs,
        (joinedRoomHistory, joinedRoomHistoryFirstUnreadTs) => ({
            joinedRoomHistory,
            messages: chaletHistory.historyToHistoryElements(joinedRoomHistory, {
                ts: joinedRoomHistoryFirstUnreadTs,
            }),
        })
    );

export const getNextRoomsToClose = createSelector(
    (store: RootState) => getFlattenRoomViews(store),
    getActiveRoomView,
    (rooms, lastActiveView) => {
        return rooms.sort((a, b) => {
            // two views for different rooms are sorted if one is last active
            if (a.roomId !== b.roomId && lastActiveView) {
                if (a.roomId === lastActiveView.roomId) {
                    return 1;
                }
                if (b.roomId === lastActiveView.roomId) {
                    return -1;
                }
                return 0;
            }
            // two views for same room are sorted if one is real time
            if (a.type === RoomType.REAL_TIME) {
                return 1;
            }
            if (b.type === RoomType.REAL_TIME) {
                return -1;
            }
            // two views for same room that are not real time are sorted by last active
            if (a === lastActiveView) {
                return 1;
            }
            if (b === lastActiveView) {
                return -1;
            }
            // otherwise preserve original order
            return 0;
        });
    }
);

export const getNextRoomToClose = createSelector(
    getNextRoomsToClose,
    rooms => rooms.first<undefined>()
);

export function getFitRooms(store: OuterStore) {
    return store.rooms.fitCols * store.rooms.fitRows || 1;
}
export function getFitCols(store: OuterStore) {
    return store.rooms.fitCols;
}
export function getFitRows(store: OuterStore) {
    return store.rooms.fitRows;
}
export function getRosterHoveredRoomId(store: OuterStore) {
    return store.rooms.rosterHoveredRoomId;
}

export const selectIsGlobalSearchVisible = createSelector(
    (store: RootState) => store.rooms.roomViews,
    rooms => {
        return rooms.has("globalSearch");
    }
);

export function getUnreadFromHereMessages(
    { rooms: store }: OuterStore,
    roomId: string,
    monologUserId: string,
    ts: number
): { tssRead: number[]; tssUnread: number[]; tssUnreadMentions: number[]; receiptTs: number } {
    const roomView = flattenRoomViews(store.roomViews).find(
        x => x.roomId === roomId && x.type === RoomType.REAL_TIME
    );
    const or = roomView && store.byOpenRoom.get(roomView.viewId);
    if (or) {
        // mark as unread just one message
        const unseen = or.joinedRoomHistory.filter(x => !x.unseen && x.ts >= ts).take(1);
        const prevMessage = or.joinedRoomHistory.findLast(x => x.ts < ts);
        const unseenMentions = unseen.filter(
            x => x.mentions && x.mentions.some(x => x.user_id === monologUserId)
        );
        return {
            tssRead: or.joinedRoomHistory
                .filter(x => x.unseen && x.ts < ts)
                .map(x => x.ts)
                .toArray(),
            tssUnread: unseen.map(x => x.ts).toArray(),
            tssUnreadMentions: unseenMentions.map(x => x.ts).toArray(),
            // receiptTs is 0 when we're marking as unread very first message in the room (this way receipt is hidden)
            // but fallback receiptTs to original ts, when previous history isn't loaded yet
            receiptTs: prevMessage
                ? prevMessage.ts
                : or.joinedRoomHistorySpinner === "loading"
                ? ts
                : 0,
        };
    }
    return { tssRead: [], tssUnread: [], tssUnreadMentions: [], receiptTs: ts };
}

export function getRoomViewEditMode(store: RootState, viewId: string, messageId?: string) {
    const roomView = flattenRoomViews(store.rooms.roomViews).find(x => x.viewId === viewId);
    const editMode = roomView && roomView.editMode;
    if (messageId && editMode && editMode.message.id !== messageId) {
        return undefined;
    }
    return editMode;
}

export const createNewForumView = RoomView({
    roomId: "new-forum",
    viewId: "new-forum",
});

export function getRosterSearchLoading(store: OuterStore, { viewId }: RoomView) {
    return store.rooms.byOpenRoom.get(viewId, byOpenRoomDefault).roomMentionsLoading;
}

export const makeSelectorMentionsOrder = () =>
    createSelector(
        (_: OuterStore, { roomView }: { roomView: RoomView }) => roomView.viewId,
        (store, _) => store.rooms.byOpenRoom,
        (viewId, byOpenRoom) => byOpenRoom.get(viewId, byOpenRoomDefault).roomMentionsOrder
    );

export function getRoomMentionInputPrefix(store: OuterStore, { roomView }: { roomView: RoomView }) {
    return store.rooms.byOpenRoom.get(roomView.viewId, byOpenRoomDefault).roomMentionsInputPrefix;
}

export function getRoomMentionsIsOpen(store: OuterStore, { roomView }: { roomView: RoomView }) {
    return store.rooms.byOpenRoom.get(roomView.viewId, byOpenRoomDefault).roomMentionsIsOpen;
}

export function getRoomMentionsSelected(store: OuterStore, { roomView }: { roomView: RoomView }) {
    return store.rooms.byOpenRoom.get(roomView.viewId, byOpenRoomDefault).roomMentionsSelected;
}

export function getPrivateRoomAddMemberInput(
    store: OuterStore,
    { roomView }: { roomView: RoomView }
) {
    return store.rooms.byOpenRoom.get(roomView.viewId, byOpenRoomDefault).addMemberInput;
}
